import { zodResolver } from "@hookform/resolvers/zod"
import type { AxiosError } from "axios"
import { useCallback, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import type { LocalMediaItem, MediaChanges } from "@/components/media/mediaUploadSection"
import { LeaveConfirmationModal } from "@/components/modals/LeaveConfirmationModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { CommunityForm } from "@/features/communities/components/CommunityForm"
import { useCommunity } from "@/features/communities/hooks/useCommunity"
import { useCommunityMembersInfinite } from "@/features/communities/hooks/useCommunityMembersInfinite"
import { useUpdateCommunity } from "@/features/communities/hooks/useCommunityMutations"
import { useCommunityRoles } from "@/features/communities/hooks/useCommunityRoles"
import {
  type CreateCommunityFormData,
  createCommunitySchema,
} from "@/features/communities/schemas/community.schema"
import type { CommunityUserWithRole, LocationData } from "@/features/communities/types"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard"
import { useTranslation } from "@/language"
import type { CommunityTag } from "../types/community-detail"

const BACKEND_TO_FRONTEND_MAP: Record<string, string> = {
  EVERYONE: "Everyone",
  FOLLOWERS: "Followers",
  COMMUNITY_MEMBERS: "Community Members",
  OWNERS: "Owners",
  MODERATORS: "Moderators",
  RITUALISTS: "Ritualists",
  MEMBERS: "Members",
}

export function EditCommunityScreen() {
  const { t } = useTranslation("communities")
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: community, isLoading, error: fetchError } = useCommunity(id || "")
  const currentUser = useAuthStore(state => state.user)

  const {
    data: membersData,
    isLoading: membersLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCommunityMembersInfinite(id || "")

  const allMembers = membersData?.pages.flatMap(page => page.members) ?? []

  const loadMoreRef = useInfiniteScroll({
    onLoadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    hasMore: !!hasNextPage,
    disabled: isFetchingNextPage,
  })

  const form = useForm<CreateCommunityFormData>({
    resolver: zodResolver(createCommunitySchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      handle: "",
      type: "public",
      isOrganization: false,
      address: "",
      description: "",
      websiteUrl: "",
      twitterUrl: "",
      instagramUrl: "",
      facebookUrl: "",
      guidelines: "",
      tags: [],
      privacySettings: {
        addToRituals: ["Owners", "Moderators", "Ritualists", "Members"],
        comment: ["Owners", "Moderators", "Ritualists", "Members"],
      },
      selectedUsers: [],
    },
  })

  const { setValue, trigger, reset } = form

  // Warn before leaving with unsaved edits or an in-flight video upload.
  const [isVideoUploading, setIsVideoUploading] = useState(false)
  const unsavedGuard = useUnsavedChangesGuard(form.formState.isDirty || isVideoUploading)

  const { data: communityRoles = [] } = useCommunityRoles()
  const ownerRole = communityRoles.find(r => r.title === "OWNER")
  const ownerRoleId = ownerRole?.id

  const [originalTags, setOriginalTags] = useState<string[]>([])
  const [originalTagsWithIds, setOriginalTagsWithIds] = useState<
    Array<{ id: string; text: string }>
  >([])
  const [originalUsers, setOriginalUsers] = useState<CommunityUserWithRole[]>([])

  const [tags, setTags] = useState<string[]>([])
  const [tagsWithIds, setTagsWithIds] = useState<Array<{ id: string; text: string }>>([])
  const [selectedUsers, setSelectedUsers] = useState<CommunityUserWithRole[]>([])
  const [removedUserIds, setRemovedUserIds] = useState<Set<string>>(new Set())
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null)
  const [communityType, setCommunityType] = useState<"public" | "private">("public")
  const [privacySettings, setPrivacySettings] = useState({
    addToRituals: ["Owners", "Moderators", "Ritualists", "Members"],
    comment: ["Owners", "Moderators", "Ritualists", "Members"],
  })

  const [mediaChanges, setMediaChanges] = useState<MediaChanges | null>(null)
  const [hasCoverImage, setHasCoverImage] = useState(false)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const updateCommunityMutation = useUpdateCommunity(id || "")

  useEffect(() => {
    if (community) {
      reset({
        name: community.name || "",
        handle: community.handle || "",
        type: (community.type.toLowerCase() || "public") as "public" | "private",
        isOrganization: community.isOrganization === true,
        address: community.streetAddress || "",
        description: community.description || "",
        websiteUrl: community.websiteUrl || "",
        twitterUrl: community.twitterUrl || "",
        instagramUrl: community.instagramUrl || "",
        facebookUrl: community.facebookUrl || "",
        guidelines: community.guidelines || "",
        tags: [],
        privacySettings: {
          addToRituals: ["Owners", "Moderators", "Ritualists", "Members"],
          comment: ["Owners", "Moderators", "Ritualists", "Members"],
        },
        selectedUsers: [],
      })

      const tagsData = community.tags.map((tag: string | CommunityTag) =>
        typeof tag === "string" ? { id: "", text: tag } : { id: tag.id, text: tag.tagText },
      )
      const tagNames = tagsData.map((t: { text: string }) => t.text)
      setTags(tagNames)
      setOriginalTags(tagNames)
      setTagsWithIds(tagsData)
      setOriginalTagsWithIds(tagsData)
      setValue("tags", tagNames)

      if (community.lat && community.long) {
        setSelectedLocation({
          address: community.streetAddress || "",
          lat: community.lat,
          lng: community.long,
          city: community.city,
          state: community.state,
          country: community.country,
          zipCode: community.zipcode || undefined,
        })
      }

      setCommunityType((community.type.toLowerCase() || "public") as "public" | "private")

      const users = community.members.map(m => {
        const isCurrentUser = currentUser?.id === m.userId
        return {
          userId: m.userId,
          name: m.name,
          email: m.email || "",
          username: m.username,
          profilePictureUrl: m.profilePictureUrl || m.avatarUrl || null,
          roleIds: m.roleIds || (m.roleId ? [m.roleId] : []),
          roleId: m.roleId || "",
          communityBuilder: m.communityBuilder,
          isCurrentUser: isCurrentUser,
        }
      })
      setSelectedUsers(users)
      setOriginalUsers(users)
      setValue("selectedUsers", users)

      const getSettingValue = (key: string) => {
        const value = (community as unknown as Record<string, unknown>)[key] as string[] | undefined
        if (!Array.isArray(value)) return ["Owners", "Moderators", "Ritualists", "Members"]
        const mapped = value.map(v => BACKEND_TO_FRONTEND_MAP[v] || v)
        // Always include Owners, even if not present in backend response
        return mapped.includes("Owners") ? mapped : ["Owners", ...mapped]
      }

      setPrivacySettings({
        addToRituals: getSettingValue("whoCanAddCommunityToRituals"),
        comment: getSettingValue("whoCanComment"),
      })
    }
  }, [community, reset, setValue, currentUser?.id])

  // Initialize mediaChanges and hasCoverImage when community data loads
  useEffect(() => {
    if (!community?.media || community.media.length === 0) return

    // Convert community media to MediaChanges format
    const mediaItems: LocalMediaItem[] = community.media.map(m => ({
      id: m.communityMediaId || m.id || "",
      uploadId: m.uploadId,
      src: m.filePath || "",
      mediaType: (m.mediaType === "IMAGE" ? "IMAGE" : "VIDEO") as "IMAGE" | "VIDEO" | "AUDIO",
      isCover: m.isCover || false,
      sortOrder: m.sortOrder || 0,
      title: m.videoTitle ?? null,
      videoTitle: m.videoTitle ?? null,
      thumbnailSrc: m.thumbnailFilePath || null,
      thumbnailFile: undefined,
      thumbnailUploadId: m.thumbnailUploadId || null,
    }))

    const orderedMediaIds = mediaItems.map(m => m.id)
    const coverMediaId = mediaItems.find(m => m.isCover)?.id || null

    setMediaChanges({
      mediaItems,
      deletedMediaIds: [],
      newFiles: [],
      coverMediaId,
      orderedMediaIds,
      videoTitles: {},
      thumbnailFiles: {},
    })
    setHasCoverImage(coverMediaId !== null)
  }, [community])

  const handleAddTag = (tag: string) => {
    const newTags = [...tags, tag]
    setTags(newTags)
    setValue("tags", newTags)
    setTagsWithIds(prev => [...prev, { id: "", text: tag }])
    trigger("tags")
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove)
    setTags(updatedTags)
    setValue("tags", updatedTags)
    setTagsWithIds(prev => prev.filter(t => t.text !== tagToRemove))
    trigger("tags")
  }

  const handleAddUsers = (users: CommunityUserWithRole[]) => {
    const newUsers = users.filter(u => u.userId !== currentUser?.id)
    const updated = [...selectedUsers, ...newUsers]
    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
    trigger("selectedUsers")
  }

  const handleRemoveUser = (userId: string) => {
    if (userId === currentUser?.id) return
    const updated = selectedUsers.filter(u => u.userId !== userId)
    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
    setRemovedUserIds(prev => new Set(prev).add(userId))
    trigger("selectedUsers")
  }

  const handleUpdateUserRoles = (userId: string, newRoleIds: string[]) => {
    if (userId === currentUser?.id) return

    const existingUser = selectedUsers.find(u => u.userId === userId)

    let updated: CommunityUserWithRole[]
    if (existingUser) {
      updated = selectedUsers.map(u => (u.userId === userId ? { ...u, roleIds: newRoleIds } : u))
    } else {
      const member = allMembers.find(m => m.id === userId)
      if (member) {
        const newUser: CommunityUserWithRole = {
          userId: member.id,
          name: member.name || null,
          email: "",
          username: member.username,
          profilePictureUrl: member.coverImage || null,
          roleIds: newRoleIds,
          roleId: newRoleIds[0] || "",
          isCurrentUser: false,
        }
        updated = [...selectedUsers, newUser]
      } else {
        return
      }
    }

    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
    trigger("selectedUsers")
  }

  const handleMediaChange = useCallback((changes: MediaChanges) => {
    setMediaChanges(changes)
  }, [])

  const handleCoverImageChange = useCallback((hasCover: boolean) => {
    setHasCoverImage(hasCover)
  }, [])

  const handleAddToRitualsChange = useCallback((selected: string[]) => {
    setPrivacySettings(prev => ({ ...prev, addToRituals: selected }))
  }, [])

  const handleCommentChange = useCallback((selected: string[]) => {
    setPrivacySettings(prev => ({ ...prev, comment: selected }))
  }, [])

  const handleSubmit = async (data: CreateCommunityFormData) => {
    if (!id) {
      toast.error("Community ID is required")
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const addedTags = tags.filter(t => !originalTags.includes(t))
      const removedTagIds = originalTagsWithIds
        .filter(
          originalTag => !tagsWithIds.some(currentTag => currentTag.text === originalTag.text),
        )
        .map(t => t.id)
        .filter(id => id !== "")

      const addedUsers = selectedUsers.filter(
        su => !originalUsers.find(ou => ou.userId === su.userId),
      )
      const updatedUsers = originalUsers
        .filter(ou => {
          const current = selectedUsers.find(cu => cu.userId === ou.userId)
          const oldRoleIds =
            ou.roleIds && ou.roleIds.length > 0 ? ou.roleIds : ou.roleId ? [ou.roleId] : []
          const newRoleIds =
            current?.roleIds && current.roleIds.length > 0
              ? current.roleIds
              : current?.roleId
                ? [current.roleId]
                : []
          return JSON.stringify(oldRoleIds) !== JSON.stringify(newRoleIds)
        })
        .map(ou => {
          const current = selectedUsers.find(cu => cu.userId === ou.userId)
          const roleIdsArray =
            current?.roleIds && current.roleIds.length > 0
              ? current.roleIds
              : current?.roleId
                ? [current.roleId]
                : []
          return {
            userId: ou.userId,
            roleIds: roleIdsArray,
          }
        })
      const removedUserIdsArray = Array.from(removedUserIds)

      const formData = new FormData()

      formData.append("name", data.name)
      formData.append("community_handle", data.handle)
      formData.append("type", communityType.toUpperCase())
      if (data.isOrganization !== undefined)
        formData.append("is_organization", String(data.isOrganization))
      if (selectedLocation?.address) formData.append("street_address", selectedLocation.address)
      if (selectedLocation?.city) formData.append("city", selectedLocation.city)
      if (selectedLocation?.state) formData.append("state", selectedLocation.state)
      if (selectedLocation?.country) formData.append("country", selectedLocation.country)
      if (selectedLocation?.zipCode) formData.append("zip_code", selectedLocation.zipCode)
      if (selectedLocation?.lat) formData.append("lat", String(selectedLocation.lat))
      if (selectedLocation?.lng) formData.append("long", String(selectedLocation.lng))
      formData.append("description", data.description)
      if (data.websiteUrl) formData.append("website_url", data.websiteUrl)
      if (data.twitterUrl) formData.append("twitter_url", data.twitterUrl)
      if (data.instagramUrl) formData.append("instagram_url", data.instagramUrl)
      if (data.facebookUrl) formData.append("facebook_url", data.facebookUrl)
      formData.append("guidelines", data.guidelines)

      const transformArray = (arr: string[]): string[] => {
        const mapping: Record<string, string> = {
          Everyone: "EVERYONE",
          Followers: "FOLLOWERS",
          "Community Members": "COMMUNITY_MEMBERS",
          Owners: "OWNERS",
          Moderators: "MODERATORS",
          Ritualists: "RITUALISTS",
          Members: "MEMBERS",
        }
        return arr.map(item => mapping[item] || item.toUpperCase().replace(" ", "_"))
      }

      formData.append(
        "who_can_add_community_to_rituals",
        JSON.stringify(transformArray(privacySettings.addToRituals)),
      )
      formData.append("who_can_comment", JSON.stringify(transformArray(privacySettings.comment)))

      if (addedTags.length > 0 || removedTagIds.length > 0) {
        formData.append(
          "tags",
          JSON.stringify({
            add: addedTags.length > 0 ? addedTags : undefined,
            remove: removedTagIds.length > 0 ? removedTagIds : undefined,
          }),
        )
      }

      const filteredUpdateUsers = updatedUsers.filter(u => u.roleIds && u.roleIds.length > 0)
      if (
        addedUsers.length > 0 ||
        filteredUpdateUsers.length > 0 ||
        removedUserIdsArray.length > 0
      ) {
        formData.append(
          "users",
          JSON.stringify({
            add:
              addedUsers.length > 0
                ? addedUsers.map(u => ({
                    user_id: u.userId,
                    role_ids:
                      u.roleIds && u.roleIds.length > 0 ? u.roleIds : u.roleId ? [u.roleId] : [],
                  }))
                : undefined,
            update:
              filteredUpdateUsers.length > 0
                ? filteredUpdateUsers.map(u => ({ user_id: u.userId, role_ids: u.roleIds }))
                : undefined,
            remove: removedUserIdsArray.length > 0 ? removedUserIdsArray : undefined,
          }),
        )
      }

      if (mediaChanges) {
        const newFiles: File[] = []
        const newFileIndexMap = new Map<string, number>()
        let newIdx = 0
        for (const item of mediaChanges.mediaItems) {
          if (item.file) {
            newFiles.push(item.file)
            newFileIndexMap.set(item.id, newIdx++)
          }
        }
        for (const file of newFiles) {
          formData.append("files", file)
        }

        const existingIdSet = new Set(
          mediaChanges.mediaItems.filter(i => !i.file && i.uploadId).map(i => i.id),
        )
        const orderedIds = mediaChanges.orderedMediaIds.map(id => {
          if (existingIdSet.has(id)) return id
          const fileIdx = newFileIndexMap.get(id)
          return fileIdx !== undefined ? `new:${fileIdx}` : id
        })
        if (orderedIds.length > 0) {
          formData.append("media_order", JSON.stringify(orderedIds))
        }

        if (mediaChanges.deletedMediaIds.length > 0) {
          formData.append("media_deleted", JSON.stringify(mediaChanges.deletedMediaIds))
        }

        if (mediaChanges.coverMediaId) {
          const coverId = mediaChanges.coverMediaId
          let resolvedCoverId = coverId
          if (!existingIdSet.has(coverId)) {
            const fileIdx = newFileIndexMap.get(coverId)
            if (fileIdx !== undefined) {
              resolvedCoverId = `new:${fileIdx}`
            }
          }
          formData.append("media_cover_id", resolvedCoverId)
        }

        if (Object.keys(mediaChanges.videoTitles).length > 0) {
          const resolvedTitles: Record<string, string> = {}
          for (const [id, title] of Object.entries(mediaChanges.videoTitles)) {
            if (existingIdSet.has(id)) {
              resolvedTitles[id] = title
            } else {
              const fileIdx = newFileIndexMap.get(id)
              if (fileIdx !== undefined) {
                resolvedTitles[`new:${fileIdx}`] = title
              }
            }
          }
          if (Object.keys(resolvedTitles).length > 0) {
            formData.append("media_titles", JSON.stringify(resolvedTitles))
          }
        }

        if (Object.keys(mediaChanges.thumbnailFiles).length > 0) {
          const thumbnailFileList: File[] = []
          const thumbnailMediaIds: string[] = []
          for (const [id, file] of Object.entries(mediaChanges.thumbnailFiles)) {
            thumbnailFileList.push(file)
            if (existingIdSet.has(id)) {
              thumbnailMediaIds.push(id)
            } else {
              const fileIdx = newFileIndexMap.get(id)
              thumbnailMediaIds.push(fileIdx !== undefined ? `new:${fileIdx}` : id)
            }
          }
          for (const file of thumbnailFileList) {
            formData.append("media_thumbnails", file)
          }
          formData.append("media_thumbnail_ids", JSON.stringify(thumbnailMediaIds))
        }
      }

      await updateCommunityMutation.mutateAsync(formData)

      toast.success(t("editCommunity.successMessage"))
      // Changes are saved -- let the post-save navigation bypass the guard.
      unsavedGuard.allowNavigation()
      navigate(`/communities/${id}`)
    } catch (err: unknown) {
      const axiosError = err as AxiosError<{ message: string }>
      const errorMessage = axiosError.response?.data?.message || t("editCommunity.errorMessage")
      setSubmitError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>{t("editCommunity.loading")}</p>
      </div>
    )
  }

  if (fetchError || !community) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-red-500">{t("editCommunity.loadingError")}</p>
      </div>
    )
  }

  return (
    <CommunityForm
      mode="edit"
      form={form}
      communityId={id}
      onUploadingChange={setIsVideoUploading}
      tags={tags}
      onAddTag={handleAddTag}
      onRemoveTag={handleRemoveTag}
      selectedUsers={selectedUsers}
      onAddUsers={handleAddUsers}
      onRemoveUser={handleRemoveUser}
      onUpdateUserRoles={handleUpdateUserRoles}
      communityType={communityType}
      onCommunityTypeChange={setCommunityType}
      privacySettings={privacySettings}
      onAddToRitualsChange={handleAddToRitualsChange}
      onCommentChange={handleCommentChange}
      selectedLocation={selectedLocation}
      onLocationSelect={setSelectedLocation}
      communityRoles={communityRoles}
      ownerRoleId={ownerRoleId}
      isSubmitting={isSubmitting}
      submitError={submitError}
      onSubmit={() => form.handleSubmit(handleSubmit)()}
      editProps={{
        community,
        mediaChanges,
        onMediaChange: handleMediaChange,
        hasCoverImage,
        onCoverImageChange: handleCoverImageChange,
        removedUserIds,
        allMembers,
        membersLoading,
        hasNextPage: !!hasNextPage,
        isFetchingNextPage,
        loadMoreRef,
      }}
      renderModals={() => (
        <LeaveConfirmationModal
          isOpen={unsavedGuard.isBlocked}
          onKeepEditing={unsavedGuard.cancelLeave}
          onLeave={unsavedGuard.confirmLeave}
        />
      )}
    />
  )
}
